"use client";

import { forwardRef, ForwardedRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import MedalIcon from "@mui/icons-material/WorkspacePremium";

import { useStyles } from "./styles";
import { QaQuizLeaderboardItem, QuizLobbyPlayer } from "@/types/quiz";
import { SubscriptionType } from "@/types/user";
import { useAppSelector } from "@/redux/store";
import { getLeaderboardDetailText, formatSeconds } from "./helpers";
import { getDisplayNumber } from "@/utils/number";
import Avatar from "@/components/common/Avatar";
import SubscriptionBadge from "@/components/profile/SubscriptionBadge";

interface QuestionAnswerLeaderboardRowProps {
  leaderboardType: "quiz" | "overall";
  leaderboardItem: QaQuizLeaderboardItem;
  ref?: ForwardedRef<HTMLDivElement>;
  player?: QuizLobbyPlayer | null;
}

const QuestionAnswerLeaderboardRow = forwardRef<
  HTMLDivElement,
  QuestionAnswerLeaderboardRowProps
>(({ leaderboardType, leaderboardItem, player = null }, ref) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const playerQuery = searchParams?.get("player");
  const user = useAppSelector((state) => state.user.user);
  const isUser = Boolean(
    (user && user.id === leaderboardItem.userId) ||
      (playerQuery && leaderboardItem.username === playerQuery) ||
      (player && leaderboardItem.username === player.name)
  );
  const { classes } = useStyles({ isUser, rank: leaderboardItem.rank });
  const displayRank = getDisplayNumber(leaderboardItem.rank);

  const handleViewProfile = () => {
    if (leaderboardItem.username) {
      router.push(`/profile/${leaderboardItem.username}`);
    }
  };

  return (
    <div className={classes.container} ref={ref}>
      <div className={classes.contentGroup}>
        <div className={classes.rank}>
          {leaderboardItem.rank < 4 ? (
            <MedalIcon className={classes.medal} />
          ) : (
            <p
              className={classes.rankLabel}
              title={leaderboardItem.rank.toString()}
            >
              {displayRank}
            </p>
          )}
        </div>
        <div
          className={classes.userDetailsContainer}
          onClick={handleViewProfile}
        >
          <div className={classes.avatarContainer}>
            <Avatar
              src={leaderboardItem.avatarUrl || ""}
              alt={leaderboardItem.username || "Anonymous"}
              className={classes.avatar}
            />
            <SubscriptionBadge
              subscriptionType={
                leaderboardItem.subscriptionType || SubscriptionType.Free
              }
              iconOnly
              className={classes.subscriptionIcon}
            />
          </div>
          <div className={classes.userDetails}>
            <p className={classes.username}>
              {leaderboardItem.username || "Anonymous"}
            </p>
            <p className={classes.resultsDetail}>
              {getLeaderboardDetailText(leaderboardType, leaderboardItem)}
            </p>
          </div>
        </div>
      </div>
      <div className={classes.contentGroup}>
        <p className={classes.score}>
          {leaderboardItem.score.toFixed(0)}
          {leaderboardType === "quiz" ? "%" : ""}
        </p>
        {leaderboardItem.timeTaken && (
          <p className={classes.timeTaken}>
            {formatSeconds(leaderboardItem.timeTaken)}
          </p>
        )}
      </div>
    </div>
  );
});

export default QuestionAnswerLeaderboardRow;

QuestionAnswerLeaderboardRow.displayName = "QuestionAnswerLeaderboardRow";
